/* oxlint-disable */
"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { memo, useCallback, useState, useMemo } from "react";

import { AiFillDollarCircle } from "react-icons/ai";
import { IoHeartSharp, IoHeartOutline } from "react-icons/io5";

import AcceptModal from "@/components/modals/AcceptModal";
import { useAuth } from "@/core/auth/client/AuthProvider";
import { updateFavoriteGames } from "@/features/casino/services";
import { routes } from "@/features/v2/constants/routes";
import { useRedirectGame } from "@/features/v2/shared/hooks/casinoHooks/useRedirecGame";
import { useAuthStore } from "@/stores/userStore";
import { userUIStore } from "@/stores/userUIStore";
import { GameFlag } from "@/stories/atoms/gameFlag/GameFlag";
import { normalizeImageUrl } from "@/utils/core";
import { isGameCurrencyMismatch } from "@/utils/currency";
import { isOptimizableImageUrl } from "@/utils/image-optimizer";

import { GameCardProps } from "./interface";

const GAME_CARD_CAROUSEL_SIZES = "(max-width: 768px) 180px, (max-width: 1200px) 220px, 300px";
const GAME_CARD_GRID_SIZES = "(max-width: 767px) 45vw, (max-width: 1023px) 32vw, (max-width: 1279px) 24vw, 303px";

function GameCard({ game, index = 0, priority = false, categorySelected = "", onGameClick, showFavoriteButton = true, showPlayButton = true, variant = "default" }: Readonly<GameCardProps>): React.ReactElement {
  const [imageError, setImageError] = useState(false);
  const [currencyModalData, setCurrencyModalData] = useState<{
    url: string;
    originalCurrency: string;
    fallbackCurrency: string;
  } | null>(null);
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const { handleGameRedirect } = useRedirectGame();
  const router = useRouter();
  const { session } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const { account, updateFavoriteGames: updateFavoriteGamesStore } = useAuthStore();
  const { toggleLoginModal } = userUIStore();
  const userCurrency = account?.currency || "USD";

  const favorites = useMemo(() => {
    return account?.favoriteGames || [];
  }, [account?.favoriteGames]);

  const isFavorite = useCallback(
    (gameCode: string): boolean => {
      return Array.isArray(favorites) && favorites.includes(gameCode);
    },
    [favorites]
  );

  const toggleFavorite = useCallback(
    async (gameCode: string): Promise<void> => {
      if (!session?.user) {
        toggleLoginModal();
        return;
      }

      if (isFavorite(gameCode)) {
        const newFavorites = favorites.filter((code) => {
          return code !== gameCode;
        });
        const updatedFavorites = await updateFavoriteGames(newFavorites);
        updateFavoriteGamesStore(updatedFavorites);
      } else {
        const newFavorites = [...favorites, gameCode];
        const updatedFavorites = await updateFavoriteGames(newFavorites);
        updateFavoriteGamesStore(updatedFavorites);
      }
    },
    [session, favorites, isFavorite, updateFavoriteGamesStore, toggleLoginModal]
  );

  const handleImageError = useCallback((): void => {
    setImageError(true);
  }, []);

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleFavorite(game.identifier);
    },
    [toggleFavorite, game.identifier]
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleGameClick = useCallback(
    (gameItem: any, gameIndex: number) => {
      if (onGameClick) {
        onGameClick(gameItem, gameIndex, categorySelected);
        return;
      }

      if (!session?.user) {
        router.push(routes.LOGIN);
        return;
      }

      const patchedGame = {
        ...gameItem,
        released_at: gameItem.released_at ?? undefined
      };

      handleGameRedirect({
        item: patchedGame,
        categorySelected,
        gameIndex,
        onCurrencyMismatch: ({ url, originalCurrency, fallbackCurrency }) => {
          setCurrencyModalData({ url, originalCurrency, fallbackCurrency });
          setIsCurrencyModalOpen(true);
        }
      });
    },
    [onGameClick, categorySelected, router, session, handleGameRedirect]
  );

  const sectionInfo = useMemo(() => {
    if (!game.sections || Object.keys(game.sections).length === 0) {
      return { color: null, text: null };
    }

    if (game.sections.recomendados) {
      return { color: "#f54180", text: "TOP" };
    }
    if (game.sections.populares) {
      return { color: "#c4841d", text: "Populares" };
    }
    if (game.sections.nuevos) {
      return { color: "#17c964", text: "Nuevos" };
    }

    return { color: null, text: null };
  }, [game.sections]);

  const imageUrl = useMemo(() => {
    return normalizeImageUrl(imageError ? null : game.thumb_url);
  }, [imageError, game.thumb_url]);

  const cardClasses = variant === "carousel" ? "group relative rounded-lg overflow-visible hover:shadow-lg w-[180px] md:w-[220px] lg:w-[300px]" : "group relative rounded-lg overflow-visible hover:shadow-lg transition-all duration-300 w-full";

  const imageClasses = variant === "carousel" ? "relative overflow-hidden bg-neutral-900 w-full rounded-lg aspect-[245/184]" : "relative overflow-hidden bg-neutral-900 w-full rounded-lg aspect-[245/184]";
  const hasCurrencyMismatch = useMemo(() => {
    return isGameCurrencyMismatch(userCurrency, game.currency);
  }, [userCurrency, game.currency]);

  return (
    <>
      {currencyModalData && <AcceptModal url={currencyModalData.url} originalCurrency={currencyModalData.originalCurrency} fallbackCurrency={currencyModalData.fallbackCurrency} isOpen={isCurrencyModalOpen} onOpenChange={setIsCurrencyModalOpen} />}
      <div
        className={cardClasses}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: variant === "carousel" && isHovered ? "scale(1.05)" : (undefined as any),
          transition: "transform 0.3s ease-in-out",
          transformOrigin: "center",
          padding: variant === "carousel" && isHovered ? "4.5px" : ""
        }}
      >
        <div className={imageClasses}>
          <Image
            src={imageUrl}
            alt={game.title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300 rounded-lg"
            onError={handleImageError}
            sizes={variant === "carousel" ? GAME_CARD_CAROUSEL_SIZES : GAME_CARD_GRID_SIZES}
            priority={priority}
            fetchPriority={priority ? "high" : "auto"}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            unoptimized={!isOptimizableImageUrl(imageUrl)}
          />

          {showPlayButton && (
            <>
              <div
                className="absolute inset-0  rounded-lg pointer-events-none"
                style={{
                  backgroundColor: isHovered ? "#00000091" : "transparent"
                }}
              />

              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => {
                    return handleGameClick(game, index);
                  }}
                  className={`bg-primary-400 hover:bg-primary-500 text-white rounded-full p-3 transition-all duration-300 transform ${isHovered ? "opacity-100 scale-100" : "opacity-0 scale-75"}`}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>

        {hasCurrencyMismatch && (
          <div className="absolute -top-2 -right-2 z-30 rounded-full bg-black/80 p-0.5 shadow-md">
            <AiFillDollarCircle className="text-xl text-yellow-400 drop-shadow-sm" />
          </div>
        )}
        {session && showFavoriteButton && (
          <div className={`absolute shadow-xl shadow-black ${isHovered ? "bottom-2" : "bottom-0"} -right-0 bg-gradient-100 text-hsecondary-50 rounded-full font-medium text-sm z-30 ${variant === "carousel" ? "text-xs" : "text-sm"} ${isFavorite(game.identifier) ? "text-red-500" : "text-hsecondary-50"}`}>
            <button type="button" onClick={handleFavoriteClick} className="p-1" aria-label={isFavorite(game.identifier) ? "Quitar de favoritos" : "Agregar a favoritos"}>
              {isFavorite(game.identifier) ? <IoHeartSharp className={`text-red-500 ${variant === "carousel" ? "text-sm" : "text-lg"}`} /> : <IoHeartOutline className={`${variant === "carousel" ? "text-sm" : "text-lg"} text-hsecondary-50`} color="black" />}
            </button>
          </div>
        )}

        {sectionInfo.color && sectionInfo.text && (
          <div className="absolute top-0 left-1">
            <GameFlag color={sectionInfo.color} text={sectionInfo.text} />
          </div>
        )}
      </div>
    </>
  );
}

export default memo(GameCard);
